import { concat } from '@ember/helper';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import t from 'ember-intl/helpers/t';
import add from 'ember-math-helpers/helpers/add';
import eq from 'ember-truth-helpers/helpers/eq';
import FileAttachmentDisplayer from 'parog-web/components/file-attachment-displayer';
import type BioExamResultsReceivedJournalEvent from 'parog-web/models/bio-exam-results-received-journal-event';
import type FileAttachment from 'parog-web/models/file-attachment';
import type SemeiaSessionService from 'parog-web/services/session';

interface Signature {
  Args: {
    isFromHprim?: unknown;
    validationStatus?: unknown;
    retrievedJournalEvent: BioExamResultsReceivedJournalEvent;
  };
}

export default class BioExamResultsAdditionalInformationsComponent extends Component<Signature> {
  @service declare session: SemeiaSessionService;

  get creatorName(): string {
    return this.args.retrievedJournalEvent.creator?.name || '';
  }

  get fileAttachmentUrl(): string {
    return `${this.#origin}/api/v1/file-attachments/`;
  }

  get hprimUrl(): string {
    return `${this.#origin}/api/v1/hprims/`;
  }

  get lastFileAttachment(): FileAttachment | undefined {
    return this.args.retrievedJournalEvent.orderedFileAttachments.at(-1);
  }

  get otherFileAttachments(): FileAttachment[] | undefined {
    return this.args.retrievedJournalEvent.orderedFileAttachments.slice(0, -1);
  }

  get shouldDisplayPreferredUnitMessage() {
    if (!this.session.medic?.isSuperUser) {
      return false;
    }

    return !!this.args.retrievedJournalEvent.patient.healthCentre
      .healthCentreLoincPreferredUnits.length;
  }

  get #origin(): string {
    const origin = window.location.origin;

    if (origin.includes('localhost')) {
      return origin.replace('4200', '3000');
    }

    return origin;
  }

  <template>
    {{#if this.lastFileAttachment}}
      <div class='flex flex-col [&:not(:first-child)]:mt-[1em]'>
        <FileAttachmentDisplayer
          @shouldDisplayDownloadLink={{true}}
          @fileAttachment={{this.lastFileAttachment}}
          @alternativeText='pièce jointe du résultat de biologie'
        />
        {{#each this.otherFileAttachments as |fileAttachment|}}
          <div class='text-right'>
            <a
              href={{concat
                this.fileAttachmentUrl
                fileAttachment.id
                '?access_token='
                this.session.accessToken
              }}
              target='_blank'
              rel='noopener noreferrer'
              data-test-file-link={{fileAttachment.index}}
            >
              <i class='external alternate icon'></i>
              Consulter la pièce-jointe
              {{add fileAttachment.index 1}}
            </a>
          </div>
        {{/each}}
      </div>
    {{/if}}

    <div class='ui info message visible'>
      {{t
        (concat 'bioExamResultsStatus.' @validationStatus)
        name=this.creatorName
      }}
    </div>
    {{#if @isFromHprim}}
      <div class='flex flex-col [&:not(:first-child)]:mt-[1em]'>
        {{#each
          @retrievedJournalEvent.apicryptMessageReceivedJournalEvents
          as |apicryptJe index|
        }}
          {{#if (eq index 0)}}
            <pre class='ui raised segment'>
              {{htmlSafe apicryptJe.escapedRawHprim}}</pre>
          {{else}}
            <div class='text-right'>
              <a
                href={{concat
                  this.hprimUrl
                  apicryptJe.id
                  '?access_token='
                  this.session.accessToken
                }}
                target='_blank'
                rel='noopener noreferrer'
                data-test-hprim-link={{index}}
              >
                <i class='external alternate icon'></i>
                Consulter le fichier HPRIM
                {{add index 1}}
              </a>
            </div>
          {{/if}}
        {{/each}}
      </div>
    {{/if}}
    {{#if @retrievedJournalEvent.showCorrection}}
      <div class='ui info message visible' data-test-correction>
        {{t 'bioExamResultCodes.showCorrection'}}
      </div>
    {{/if}}
    {{#if this.shouldDisplayPreferredUnitMessage}}
      <div class='ui pink message !bg-pink-light !text-pink visible'>
        Ce formulaire doit être complété avec les données dans les unités de
        référence de Wise. Le centre de soins du patient verra les données dans
        leurs unités de préférence configurées.
      </div>
    {{/if}}

    {{#if @retrievedJournalEvent.patient.hasPreferredUnitCachePending}}
      <div class='ui pink message !bg-pink-light !text-pink visible !mt-2'>
        La mise à jour des unités d'affichage est en cours. Certaines données
        peuvent temporairement s'afficher dans des unités différentes.
      </div>
    {{/if}}
  </template>
}
